import { useState } from "react";

// ─── Quote Logic ────────────────────────────────────────────────────────────
function getQuote(partnershipType, count) {
  const n = parseInt(count, 10);
  if (!n || n <= 0) return null;

  if (partnershipType === "chaplain") {
    return {
      type: "revenue_share",
      tier: "Revenue Share Partnership",
      price: null,
      monthly: null,
      message: "Contact us to structure your deal.",
    };
  }
  if (partnershipType === "bookstore") {
    // Bookstore: 35% commission on $20/mo subscriptions
    const monthly = n * 20 * 0.35;
    return {
      type: "revenue_share",
      tier: "Christian Bookstore Partner",
      price: "35% recurring commission",
      monthly: monthly,
      message: `Based on ${n} referred subscribers at $20/month, you'd earn $${monthly.toFixed(0)}/month in recurring revenue.`,
    };
  }
  if (["influencer", "mindfulness", "nonprofit"].includes(partnershipType)) {
    return {
      type: "contact",
      tier: "Custom Partnership",
      price: null,
      monthly: null,
      message: "Contact us to structure the right partnership for your organization.",
    };
  }

  if (partnershipType === "hrm") {
    if (n < 100)
      return { tier: "Standard", price: "$20 PEPM", monthly: n * 20, type: "fixed" };
    if (n < 500)
      return { tier: "Group", price: "$17 PEPM", monthly: n * 17, type: "fixed" };
    return { tier: "Enterprise", price: "$15 PEPM", monthly: n * 15, type: "fixed" };
  }

  // hcsm, ebs, insureco, peo, rehab, ltc
  if (n <= 50000)
    return { tier: "Standard", price: "$12 PMPM", monthly: n * 12, type: "fixed" };
  if (n <= 500000)
    return { tier: "Enhanced", price: "$10 PMPM", monthly: n * 10, type: "fixed" };
  return { tier: "Enterprise", price: "Custom", monthly: null, type: "enterprise" };
}

function formatCurrency(n) {
  if (!n) return null;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n);
}

// ─── Label helpers ───────────────────────────────────────────────────────────
const SEGMENT_LABELS = {
  hcsm: "Health Care Sharing Ministry",
  ebs: "Employee Benefit Solutions",
  hrm: "Human Resource Management",
  insureco: "Insurance Company",
  peo: "Professional Employer Organization",
  rehab: "Rehabilitation & Recovery Facility",
  chaplain: "Corporate Chaplaincy (White-Label)",
  ltc: "Long-Term Care Facility",
  influencer: "Social Media Influencer / Creator",
  mindfulness: "Mindfulness Certification Program",
  nonprofit: "Faith-Based Nonprofit Organization",
  bookstore: "Christian Bookstore",
};

const ORG_NAME_LABELS = {
  bookstore: "Bookstore Name",
  influencer: "Creator / Brand Name",
};
function orgNameLabel(type) {
  return ORG_NAME_LABELS[type] || "Organization Name";
}

const ORG_NAME_PLACEHOLDERS = {
  bookstore: "Acme Christian Bookstore",
  influencer: "@yourhandle or Your Brand",
  chaplain: "Acme Chaplaincy Services",
  nonprofit: "Acme Faith Foundation",
  mindfulness: "Acme Mindfulness Institute",
};
function orgNamePlaceholder(type) {
  return ORG_NAME_PLACEHOLDERS[type] || "Acme Health Group";
}

const TITLE_PLACEHOLDERS = {
  bookstore: "Owner / Manager",
  influencer: "Founder / Creator",
  chaplain: "Director of Chaplaincy",
  nonprofit: "Executive Director",
  mindfulness: "Program Director",
};
function titlePlaceholder(type) {
  return TITLE_PLACEHOLDERS[type] || "VP of Benefits";
}

const COUNT_PLACEHOLDERS = {
  bookstore: "e.g. 2500",
  influencer: "e.g. 100000",
  chaplain: "e.g. 5000",
  nonprofit: "e.g. 25",
  mindfulness: "e.g. 200",
};
function countPlaceholder(type) {
  return COUNT_PLACEHOLDERS[type] || "e.g. 5000";
}

const COUNT_LABEL = {
  hcsm: "Member Count",
  insureco: "Member Count",
  rehab: "Patient Count",
  chaplain: "Employee / Client Count",
  ltc: "Resident Count",
  influencer: "Audience / Follower Count",
  mindfulness: "Client / Student Count",
  nonprofit: "Staff Count",
  bookstore: "Number of Emails in Customer Database",
};
function countLabel(type) {
  return COUNT_LABEL[type] || "Employee Count";
}

// ─── Component ───────────────────────────────────────────────────────────────
export default function QuoteTool({ partnershipType = "hcsm" }) {
  const [form, setForm] = useState({
    orgName: "",
    contactName: "",
    title: "",
    email: "",
    count: "",
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [quote, setQuote] = useState(null);
  const [apiError, setApiError] = useState("");

  // ── Validation ──
  function validate() {
    const e = {};
    if (!form.orgName.trim()) e.orgName = "Organization name is required.";
    if (!form.contactName.trim()) e.contactName = "Contact name is required.";
    if (!form.title.trim()) e.title = "Title is required.";
    if (!form.email.trim()) {
      e.email = "Email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      e.email = "Enter a valid email address.";
    }
    if (!form.count || parseInt(form.count, 10) <= 0)
      e.count = `${countLabel(partnershipType)} must be greater than 0.`;
    return e;
  }

  function handleChange(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  }

  // ── Submit ──
  async function handleSubmit(e) {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }

    const computed = getQuote(partnershipType, form.count);
    setStatus("loading");
    setApiError("");

    try {
      const res = await fetch(window.location.origin + "/api/v1/quote-lead", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          partnershipType,
          segmentLabel: SEGMENT_LABELS[partnershipType] || partnershipType,
          quote: computed,
        }),
      });

      if (!res.ok) throw new Error(`Server error: ${res.status}`);

      setQuote(computed);
      setStatus("success");
      setTimeout(() => document.getElementById('quote-tool')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    } catch (err) {
      console.error(err);
      // Still show the quote even if the email API fails
      setQuote(computed);
      setStatus("success");
      setTimeout(() => document.getElementById('quote-tool')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
      setApiError(
        "Your quote is ready below. Note: confirmation email may be delayed — we'll follow up manually."
      );
    }
  }

  return (
    <>
      <style>{`
        .qt-wrap {
          background: #F0F4F8;
          border-top: 4px solid #4f46e5;
          border-radius: 16px;
          padding: 48px 40px;
          max-width: 680px;
          margin: 0 auto;
          font-family: 'Segoe UI', system-ui, sans-serif;
        }
        .qt-heading {
          font-size: 1.75rem;
          font-weight: 700;
          color: #1e3a5f;
          margin: 0 0 6px 0;
          line-height: 1.2;
        }
        .qt-subhead {
          font-size: 0.95rem;
          color: #4b5563;
          margin: 0 0 32px 0;
        }
        .qt-field { margin-bottom: 20px; }
        .qt-label {
          display: block;
          font-size: 0.82rem;
          font-weight: 600;
          color: #1e3a5f;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          margin-bottom: 6px;
        }
        .qt-input {
          width: 100%;
          padding: 12px 16px;
          border: 1.5px solid #d1d5db;
          border-radius: 8px;
          font-size: 0.97rem;
          color: #1e3a5f;
          background: #fff;
          outline: none;
          transition: border-color 0.2s;
          box-sizing: border-box;
        }
        .qt-input:focus { border-color: #4f46e5; }
        .qt-input.error { border-color: #ef4444; }
        .qt-error-msg {
          font-size: 0.78rem;
          color: #ef4444;
          margin-top: 4px;
          display: block;
        }
        .qt-badge {
          display: inline-block;
          background: #e0e7ff;
          color: #4f46e5;
          font-size: 0.75rem;
          font-weight: 700;
          padding: 4px 10px;
          border-radius: 999px;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          margin-bottom: 20px;
        }
        .qt-btn {
          width: 100%;
          padding: 15px;
          background: #4f46e5;
          color: #fff;
          border: none;
          border-radius: 10px;
          font-size: 1rem;
          font-weight: 700;
          cursor: pointer;
          transition: background 0.2s, transform 0.1s;
          letter-spacing: 0.02em;
          margin-top: 8px;
        }
        .qt-btn:hover { background: #4338ca; }
        .qt-btn:active { transform: scale(0.99); }
        .qt-btn:disabled { background: #9ca3af; cursor: not-allowed; }

        /* Result card */
        .qt-result {
          background: #1e3a5f;
          border-radius: 14px;
          padding: 36px 32px;
          color: #F0F4F8;
          animation: qtFadeIn 0.4s ease;
        }
        .qt-result-title {
          font-size: 1.1rem;
          font-weight: 700;
          color: #a5b4fc;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          margin: 0 0 4px 0;
        }
        .qt-result-org {
          font-size: 1.4rem;
          font-weight: 800;
          color: #fff;
          margin: 0 0 24px 0;
        }
        .qt-result-row {
          display: flex;
          justify-content: space-between;
          align-items: baseline;
          border-bottom: 1px solid rgba(255,255,255,0.1);
          padding: 10px 0;
          font-size: 0.92rem;
        }
        .qt-result-row:last-of-type { border-bottom: none; }
        .qt-result-label { color: #94a3b8; }
        .qt-result-value { font-weight: 700; color: #fff; }
        .qt-result-highlight {
          font-size: 2rem;
          font-weight: 800;
          color: #059669;
          margin: 20px 0 4px 0;
        }
        .qt-result-monthly-label {
          font-size: 0.8rem;
          color: #94a3b8;
          margin-bottom: 24px;
        }
        .qt-result-note {
          background: rgba(79,70,229,0.2);
          border-left: 3px solid #4f46e5;
          padding: 12px 16px;
          border-radius: 6px;
          font-size: 0.85rem;
          color: #c7d2fe;
          margin-top: 20px;
        }
        .qt-result-cta {
          display: block;
          margin-top: 24px;
          text-align: center;
          padding: 13px;
          background: #059669;
          color: #fff;
          border-radius: 8px;
          font-weight: 700;
          font-size: 0.95rem;
          text-decoration: none;
          transition: background 0.2s;
        }
        .qt-result-cta:hover { background: #047857; }
        .qt-api-warn {
          background: #fef3c7;
          border: 1px solid #fcd34d;
          border-radius: 8px;
          padding: 10px 14px;
          font-size: 0.82rem;
          color: #92400e;
          margin-bottom: 16px;
        }
        @keyframes qtFadeIn {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @media (max-width: 640px) {
          .qt-wrap { padding: 32px 20px; }
          .qt-heading { font-size: 1.4rem; }
          .qt-result { padding: 28px 20px; }
          .qt-result-highlight { font-size: 1.6rem; }
        }
      `}</style>

      <div className="qt-wrap" id="quote-tool">
        {status !== "success" ? (
          <>
            <span className="qt-badge">{SEGMENT_LABELS[partnershipType] || partnershipType}</span>
            <h2 className="qt-heading">Get Your Instant Quote</h2>
            <p className="qt-subhead">
              Fill out the form below and see your estimated partnership investment in seconds.
              No commitment required.
            </p>

            <form onSubmit={handleSubmit} noValidate>
              <div className="qt-field">
                <label className="qt-label">{orgNameLabel(partnershipType)}</label>
                <input
                  className={`qt-input${errors.orgName ? " error" : ""}`}
                  type="text"
                  placeholder={orgNamePlaceholder(partnershipType)}
                  value={form.orgName}
                  onChange={(e) => handleChange("orgName", e.target.value)}
                />
                {errors.orgName && <span className="qt-error-msg">{errors.orgName}</span>}
              </div>

              <div className="qt-field">
                <label className="qt-label">Contact Name</label>
                <input
                  className={`qt-input${errors.contactName ? " error" : ""}`}
                  type="text"
                  placeholder="Jane Smith"
                  value={form.contactName}
                  onChange={(e) => handleChange("contactName", e.target.value)}
                />
                {errors.contactName && <span className="qt-error-msg">{errors.contactName}</span>}
              </div>

              <div className="qt-field">
                <label className="qt-label">Title / Position</label>
                <input
                  className={`qt-input${errors.title ? " error" : ""}`}
                  type="text"
                  placeholder={titlePlaceholder(partnershipType)}
                  value={form.title}
                  onChange={(e) => handleChange("title", e.target.value)}
                />
                {errors.title && <span className="qt-error-msg">{errors.title}</span>}
              </div>

              <div className="qt-field">
                <label className="qt-label">Work Email</label>
                <input
                  className={`qt-input${errors.email ? " error" : ""}`}
                  type="email"
                  placeholder="jane@company.com"
                  value={form.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                />
                {errors.email && <span className="qt-error-msg">{errors.email}</span>}
              </div>

              <div className="qt-field">
                <label className="qt-label">{countLabel(partnershipType)}</label>
                <input
                  className={`qt-input${errors.count ? " error" : ""}`}
                  type="number"
                  min="1"
                  placeholder={countPlaceholder(partnershipType)}
                  value={form.count}
                  onChange={(e) => handleChange("count", e.target.value)}
                />
                {errors.count && <span className="qt-error-msg">{errors.count}</span>}
              </div>

              <button className="qt-btn" type="submit" disabled={status === "loading"}>
                {status === "loading" ? "Calculating…" : "Get My Instant Quote →"}
              </button>
            </form>
          </>
        ) : (
          <>
            {apiError && <div className="qt-api-warn">{apiError}</div>}
            <div className="qt-result">
              <p className="qt-result-title">Your Quote</p>
              <p className="qt-result-org">{form.orgName}</p>

              {quote?.type === "revenue_share" ? (
                <>
                  <div className="qt-result-row">
                    <span className="qt-result-label">Partnership Model</span>
                    <span className="qt-result-value">White-Label Revenue Share</span>
                  </div>
                  <div className="qt-result-row">
                    <span className="qt-result-label">Segment</span>
                    <span className="qt-result-value">{SEGMENT_LABELS[partnershipType]}</span>
                  </div>
                  <div className="qt-result-note">
                    Revenue Share Partnership — {quote.message} Someone will reach out within 1
                    business day to structure a deal that fits your organization.
                  </div>
                </>
              ) : quote?.type === "enterprise" ? (
                <>
                  <div className="qt-result-row">
                    <span className="qt-result-label">Tier</span>
                    <span className="qt-result-value">Enterprise</span>
                  </div>
                  <div className="qt-result-row">
                    <span className="qt-result-label">{countLabel(partnershipType)}</span>
                    <span className="qt-result-value">
                      {parseInt(form.count).toLocaleString()}
                    </span>
                  </div>
                  <div className="qt-result-row">
                    <span className="qt-result-label">Pricing</span>
                    <span className="qt-result-value">Custom</span>
                  </div>
                  <div className="qt-result-note">
                    Your organization qualifies for our Enterprise tier with custom pricing. A
                    partnership specialist will reach out within 1 business day.
                  </div>
                </>
              ) : (
                <>
                  <div className="qt-result-row">
                    <span className="qt-result-label">Tier</span>
                    <span className="qt-result-value">{quote?.tier}</span>
                  </div>
                  <div className="qt-result-row">
                    <span className="qt-result-label">{countLabel(partnershipType)}</span>
                    <span className="qt-result-value">
                      {parseInt(form.count).toLocaleString()}
                    </span>
                  </div>
                  <div className="qt-result-row">
                    <span className="qt-result-label">Rate</span>
                    <span className="qt-result-value">{quote?.price}</span>
                  </div>
                  <p className="qt-result-highlight">{formatCurrency(quote?.monthly)}</p>
                  <p className="qt-result-monthly-label">estimated per month</p>
                  <div className="qt-result-note">
                    A confirmation will be sent to <strong>{form.email}</strong>. Someone will follow up within 1 business day to discuss next steps.
                  </div>
                </>
              )}

              <a
                href="mailto:partnerships@freehappyone.com"
                className="qt-result-cta"
              >
                Questions? Email partnerships@freehappyone.com
              </a>
            </div>
          </>
        )}
      </div>
    </>
  );
}
